// @rpg-unit/look-presets: look presets (tier 3; composition framework, decision 0077), cut from spike 002's
// `content/tables/looks.json` and `daylight.json`. The low-poly valley look (spike 002's decision 0034: the green-gold
// valley) for @rpg/look's low-poly renderer, and its colour script by the hour:
//   - `valley-day` (the `looks` table): @rpg/look's LowPolySceneLook, the palette content colours come from (the
//     hero, villagers, enemies, grass, path, stone, trees), the sky gradient, the distance and height haze, the warm
//     low sun, the sky and ground fill, exposure and glow;
//   - `valley` (the `daylight` table): @rpg/look's DayTable, thirteen keys over the day (sky, sun, fill, haze,
//     exposure, lamps, stars, shafts, clouds), clustered around sunrise and sunset, the sun and moon's orbit, the
//     weather's modifiers (clear, cloud, rain), indoor light profiles (cottage, home, forge, shop, crypt), the light
//     shafts' brightness, and where the rain emitter sits. Noon is the valley-day look exactly.
// The rain's particles are a reference into the game's `emitters` table (`rain.emitter`), the way a status preset's
// name is a reference into its strings: a game using the daylight preset supplies a `rain` emitter.
//
// A game takes the presets three ways:
//   - as content: list this package in its content folder's `include.json` (@rpg/content reads `src/content`) or add
//     `LOOK_PRESET_CONTENT` to the tables it loads in the browser: the `looks` and `daylight` tables;
//   - as @rpg/look values: `sceneLook(id)` (a LowPolySceneLook), `daylightParts(id)` (the DayTable, the shafts'
//     settings and the rain's placement);
//   - tuned by perceptual knobs: `tuneLooks(rows, knobs)` and `tuneDaylight(rows, knobs)`;
//   - overridden by the game's own values (0.3.0, IMP-0023): its colours for named palette entries, its values for some
//     hour keys, its factors for some weathers, as rows of a `look-overrides` table (this unit's schema) merged over
//     the unit's rows by `applyLookOverrides(tables)` before the registry loads them (overrides.ts).
import type { DayTable, LowPolySceneLook } from "@rpg/look";
import looksFile from "./content/tables/looks.json";
import looksSchema from "./content/schemas/looks.schema.json";
import daylightFile from "./content/tables/daylight.json";
import daylightSchema from "./content/schemas/daylight.schema.json";
import lookOverridesSchema from "./content/schemas/look-overrides.schema.json";

/** This unit's version (the manifest's and package.json's). */
export const LOOK_PRESETS_VERSION = "0.3.0" as const;

/** A scene look preset: @rpg/look's LowPolySceneLook with its id. */
export type ScenePreset = LowPolySceneLook & { id: string };

/** A colour script preset: @rpg/look's DayTable with its id, the shafts' brightness, the rain's placement and the
 *  reason for its numbers. */
export type DaylightPreset = DayTable & {
  id: string;
  shafts: { cards: number; pass: number; samples: number };
  rain: { emitter: { table: string; id: string }; above: number; ahead: number };
  reason: string;
};

/** The scene looks (`{ table: "looks", version, rows }`). */
export const LOOKS_TABLE = looksFile as unknown as { table: "looks"; version: number; rows: ScenePreset[] };
/** The colour scripts (`{ table: "daylight", version, rows }`). */
export const DAYLIGHT_TABLE = daylightFile as unknown as { table: "daylight"; version: number; rows: DaylightPreset[] };

/** The content this unit adds to a game, in @rpg/content's shape: its tables and their schemas by table name, and the
 *  schema of the `look-overrides` table a game may write (it ships no rows of that table). */
export const LOOK_PRESET_CONTENT = {
  package: "@rpg-unit/look-presets",
  version: LOOK_PRESETS_VERSION,
  tables: [LOOKS_TABLE, DAYLIGHT_TABLE],
  schemas: { looks: looksSchema as Record<string, unknown>, daylight: daylightSchema as Record<string, unknown>, "look-overrides": lookOverridesSchema as Record<string, unknown> },
} as const;

/** The scene look `id` (default `valley-day`) as @rpg/look's LowPolySceneLook. */
export function sceneLook(id = "valley-day", rows: readonly ScenePreset[] = LOOKS_TABLE.rows): LowPolySceneLook {
  const row = rows.find((r) => r.id === id);
  if (!row) throw new Error(`look presets: no look "${id}"`);
  const { id: _id, ...look } = row;
  return look;
}

/** The colour script `id` (default `valley`) as @rpg/look's DayTable, with its shafts' settings and the rain's
 *  placement (its emitter is a reference into the game's emitters table). */
export function daylightParts(id = "valley", rows: readonly DaylightPreset[] = DAYLIGHT_TABLE.rows): { table: DayTable; shafts: DaylightPreset["shafts"]; rain: DaylightPreset["rain"] } {
  const row = rows.find((r) => r.id === id);
  if (!row) throw new Error(`look presets: no daylight "${id}"`);
  const { reason: _r, shafts, rain, id: _id, ...table } = row;
  return { table, shafts, rain };
}

/** The perceptual knobs (rule 13): what a person would say, and what each changes. */
export const LOOK_PRESET_KNOBS = {
  brightness: "brighter or darker: scales the exposure (the look's and every hour's; 1 as tuned, 1.1 a tenth brighter).",
  haze: "hazier or clearer: scales the haze's density (the look's and every hour's; 1 as tuned, 0.5 half as hazy).",
  sun: "a stronger or softer sun: scales the sun's intensity against the fill (the look's and every hour's key light; 1 as tuned).",
} as const;

/** Knob values; each defaults to 1 (as tuned). */
export interface LookKnobs {
  brightness?: number;
  haze?: number;
  sun?: number;
}

const round = (x: number): number => Math.round(x * 1e6) / 1e6;
function knobsOf(k: LookKnobs): Required<LookKnobs> {
  const out = { brightness: k.brightness ?? 1, haze: k.haze ?? 1, sun: k.sun ?? 1 };
  for (const [n, v] of Object.entries(out)) if (!(v > 0)) throw new Error(`look presets: ${n} must be > 0 (got ${v})`);
  return out;
}

/** The scene looks turned by the knobs (new rows; the input is untouched). As tuned, equal to the input. */
export function tuneLooks(rows: readonly ScenePreset[], knobs: LookKnobs = {}): ScenePreset[] {
  const k = knobsOf(knobs);
  return rows.map((r) => {
    const out: ScenePreset = structuredClone(r);
    if (k.brightness !== 1) out.exposure = round(r.exposure * k.brightness);
    if (k.haze !== 1) out.haze = { ...out.haze, density: round(r.haze.density * k.haze) };
    if (k.sun !== 1) out.sun = { ...out.sun, intensity: round(r.sun.intensity * k.sun) };
    return out;
  });
}

/** The colour scripts turned by the same knobs, hour by hour (new rows). As tuned, equal to the input; tuned alike,
 *  noon stays equal to the tuned scene look. */
export function tuneDaylight(rows: readonly DaylightPreset[], knobs: LookKnobs = {}): DaylightPreset[] {
  const k = knobsOf(knobs);
  return rows.map((r) => {
    const out: DaylightPreset = structuredClone(r);
    if (k.brightness === 1 && k.haze === 1 && k.sun === 1) return out;
    out.keys = r.keys.map((key) => ({
      ...structuredClone(key),
      exposure: round(key.exposure * k.brightness),
      haze: { ...key.haze, density: round(key.haze.density * k.haze) },
      sun: { ...key.sun, intensity: round(key.sun.intensity * k.sun) },
    }));
    return out;
  });
}

export {
  applyLookOverrides,
  lookOverrideIssues,
  overrideDaylight,
  overrideDaylights,
  overrideLook,
  overrideLooks,
  OVERRIDE_FAULTS,
  type DayKeyOverride,
  type LookOverrideRow,
  type OverridableTable,
  type PresetRef,
  type WeatherOverride,
} from "./overrides";
